import {
  allowedTransitions,
  applyTransition,
  type BookingStatus,
  type BookingTransition,
  canEditPrices,
  canSettle,
  formatEuros,
  formatInBrussels,
  isTerminal,
} from "@bivak/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useState } from "react";
import { StatusChip } from "../../components/beheer/StatusChip.js";
import { nl } from "../../copy/nl.js";
import {
  type AdminBookingDetail,
  ApiError,
  fetchAdminBlokkades,
  fetchAdminBooking,
  fetchAdminBookings,
  type GegevensWijziging,
  patchGegevens,
  postBookingTransition,
} from "../../lib/api.js";
import { overlappendeBezetting, verhuurtypeVan } from "../../lib/beheer.js";
import { asCents, formatDateRange, formatDayLong } from "../../lib/format.js";

const route = getRouteApi("/beheer/boekingen/$boekingId");

/**
 * Which buttons exist comes from the state machine, never from this file
 * (sketch decision 5a). `expire` is the scheduler's job (0.2c), so the admin
 * does not get a button for it.
 */
function adminActions(status: string): BookingTransition[] {
  // `expire` is the scheduler's job (0.2c); `settle` and `reopen` carry the
  // settlement with them and go through the eindafrekening screen.
  return allowedTransitions(status as BookingStatus).filter(
    (t) => t !== "expire" && t !== "settle" && t !== "reopen",
  );
}

/**
 * Ask before a transition into a terminal state — those cannot be undone.
 * Derived from the state machine (a dry run through `applyTransition`), not a
 * hand-kept list that drifts when a state is added.
 */
function isOnomkeerbaar(status: BookingStatus, transition: BookingTransition): boolean {
  const probe = applyTransition(status, transition, {
    bookingId: "probe",
    actorId: "probe",
    at: new Date(0),
  });
  return probe.ok && isTerminal(probe.status);
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-lijn bg-kaart p-4">
      <h2 className="mb-3 font-bold font-display text-[14.5px]">{title}</h2>
      {children}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-x-3 py-1 text-[13px]">
      <dt className="text-mist">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

/** SCHERM 5 in the sketch: one booking, everything about it. Read-only slice. */
export function BeheerBoekingDetailPage() {
  const { boekingId } = route.useParams();
  const [bewerken, setBewerken] = useState(false);
  const queryClient = useQueryClient();
  const overgang = useMutation({
    mutationFn: (transition: BookingTransition) => postBookingTransition(boekingId, transition),
    onSettled: () => {
      // Also after a 409: the conflict means the truth changed — refetch it.
      void queryClient.invalidateQueries({ queryKey: ["admin-booking", boekingId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      // Option and cancellation change what the public calendar shows.
      void queryClient.invalidateQueries({ queryKey: ["availability"] });
    },
  });
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["admin-booking", boekingId],
    queryFn: () => fetchAdminBooking(boekingId),
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (isPending) return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  if (error || !data) {
    const gone = error instanceof ApiError && (error.status === 404 || error.status === 400);
    return (
      <div className="py-16 text-center">
        <p className="text-mist">{gone ? nl.beheer.detail.nietGevonden : nl.algemeen.fout}</p>
        {gone ? (
          <Link to="/beheer" className="mt-4 inline-block font-semibold text-rood text-sm">
            ← {nl.beheer.detail.terug}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 min-h-11 rounded-lg bg-rood px-5 font-semibold text-kaart text-sm"
          >
            {nl.algemeen.opnieuwProberen}
          </button>
        )}
      </div>
    );
  }

  const total = data.lines.reduce((sum, line) => sum + line.amountCents, 0);
  const booked = data.headcounts.find((headcount) => headcount.kind === "booked");
  const frozen = canEditPrices(data.status as BookingStatus) === false;
  const actions = adminActions(data.status);
  // The primary style goes to the first action that does NOT end the booking:
  // on a confirmed booking `cancel` sorts before `record_stay`, and the big
  // red button must never be the one that frees the dates.
  const primair = actions.find((t) => !isOnomkeerbaar(data.status as BookingStatus, t));

  return (
    <div>
      <Link to="/beheer" className="font-mono text-[11px] text-mist uppercase tracking-widest">
        {nl.beheer.detail.terug} /
      </Link>
      <div className="mt-1 mb-1 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-bold font-display text-xl tracking-tight">
          {data.partyName}{" "}
          <span className="ml-1 inline-block align-[3px]">
            <StatusChip
              status={data.status}
              label={data.isSystemParty ? nl.beheer.lijst.eigenWerking : undefined}
            />
          </span>
        </h1>
        {(actions.length > 0 || canSettle(data.status as BookingStatus)) && (
          <div className="flex flex-wrap gap-2">
            {canSettle(data.status as BookingStatus) && (
              <Link
                to="/beheer/boekingen/$boekingId/afrekening"
                params={{ boekingId }}
                className="inline-flex min-h-11 items-center rounded-lg bg-rood px-4 font-semibold text-kaart text-sm"
              >
                {nl.beheer.acties.settle}
              </Link>
            )}
            {actions.map((transition) => (
              <button
                key={transition}
                type="button"
                disabled={overgang.isPending}
                onClick={() => {
                  if (
                    isOnomkeerbaar(data.status as BookingStatus, transition) &&
                    !window.confirm(nl.beheer.acties.zeker(nl.beheer.acties[transition]))
                  ) {
                    return;
                  }
                  overgang.mutate(transition);
                }}
                className={`min-h-11 rounded-lg px-4 font-semibold text-sm disabled:opacity-50 ${
                  transition === primair
                    ? "bg-rood text-kaart"
                    : "border-2 border-lijn bg-transparent text-inkt"
                }`}
              >
                {nl.beheer.acties[transition]}
              </button>
            ))}
          </div>
        )}
      </div>
      {overgang.isError && (
        <p className="mb-2 rounded-lg bg-rood/10 px-3 py-2 font-semibold text-[12.5px] text-rood">
          {overgang.error instanceof ApiError ? overgang.error.message : nl.algemeen.fout}
        </p>
      )}
      {data.status === "enquiry" && <ConflictWaarschuwing data={data} />}
      <KopieerBericht data={data} totaalCents={total} />
      {data.balanceCents !== null && (
        <p className="mb-2">
          <Link
            to="/beheer/boekingen/$boekingId/afrekening"
            params={{ boekingId }}
            className="font-semibold text-[12.5px] text-rood"
          >
            {nl.beheer.afrekening.bekijk} →
          </Link>
        </p>
      )}
      {data.status === "option" && data.optionExpiresAt !== null && (
        <p className="mb-4 font-semibold text-[12.5px] text-amber">
          ⏱ {nl.beheer.detail.optieVervaltOp(formatInBrussels(new Date(data.optionExpiresAt)))}
        </p>
      )}

      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <Card title={nl.beheer.detail.verblijf}>
            <dl>
              <KV label={nl.beheer.detail.wat}>
                {(() => {
                  const type = verhuurtypeVan(data);
                  const naam = type
                    ? nl.kalender.types[type].naam
                    : data.stayType === "day"
                      ? nl.beheer.detail.dagverhuur
                      : nl.beheer.detail.overnachting;
                  return `${naam} · ${data.spaceName}`;
                })()}
              </KV>
              <KV label={nl.beheer.detail.aankomst}>
                <span className="font-mono text-[12.5px]">
                  {formatDayLong(data.arrivalDate, data.arrivalTime)}
                </span>
              </KV>
              <KV label={nl.beheer.detail.vertrek}>
                <span className="font-mono text-[12.5px]">
                  {formatDayLong(data.departureDate, data.departureTime)}
                </span>
              </KV>
              {!bewerken && data.purpose && <KV label={nl.beheer.detail.doel}>{data.purpose}</KV>}
              {booked && (
                <KV label={nl.beheer.detail.personen}>
                  <span className="font-mono text-[12.5px]">
                    {booked.total} {nl.beheer.detail.aangevraagd} · {booked.under31}{" "}
                    {nl.beheer.detail.onder31}
                  </span>
                </KV>
              )}
              {!bewerken && (
                <KV label={nl.beheer.detail.contact}>
                  {data.party.contactName}
                  <span className="block font-mono text-[12px] text-mist">
                    {data.party.contactEmail} · {data.party.contactPhone}
                  </span>
                </KV>
              )}
              {/* The renter's own words from the form (0.22): detail only,
                  deliberately absent from the list rows. */}
              {data.renterReference && (
                <KV label={nl.beheer.detail.referentie}>
                  <span className="font-mono text-[12.5px]">{data.renterReference}</span>
                </KV>
              )}
              {data.renterNote && (
                <KV label={nl.beheer.detail.bericht}>
                  <span className="whitespace-pre-wrap">{data.renterNote}</span>
                </KV>
              )}
            </dl>
            {data.status !== "closed" &&
              (bewerken ? (
                <WijzigGegevens
                  boekingId={boekingId}
                  data={data}
                  onKlaar={() => setBewerken(false)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setBewerken(true)}
                  className="mt-2 min-h-11 font-semibold text-rood text-sm"
                >
                  {nl.beheer.detail.wijzig}
                </button>
              ))}
          </Card>

          <Card title={nl.beheer.detail.prijsTitel}>
            {data.lines.length === 0 ? (
              <p className="text-[13px] text-mist">{nl.beheer.detail.geenPrijs}</p>
            ) : (
              <>
                {data.lines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-baseline justify-between gap-4 border-lijn border-b border-dashed py-1.5 text-[13px]"
                  >
                    <span>
                      {line.description}
                      <span className="block text-[11px] text-mist">
                        {line.quantity} × {formatEuros(asCents(line.unitCents))}
                      </span>
                    </span>
                    <span className="font-mono">{formatEuros(asCents(line.amountCents))}</span>
                  </div>
                ))}
                <div className="flex items-baseline justify-between pt-2.5 font-bold">
                  <span>{nl.beheer.detail.totaal}</span>
                  <span className="font-mono text-[16px]">{formatEuros(asCents(total))}</span>
                </div>
                <p className="mt-2 text-[11.5px] text-mist">
                  {frozen ? nl.beheer.detail.prijsBevroren : nl.beheer.detail.prijsRichtprijs}
                </p>
              </>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card title={nl.beheer.detail.verloop}>
            <ol className="flex flex-col">
              {[...data.timeline].reverse().map((event, index) => (
                <li key={event.seq} className="relative pb-4 pl-6 last:pb-0">
                  <i
                    className={`absolute top-1 left-0 block h-2 w-2 rounded-full ${
                      index === 0 ? "bg-rood" : "bg-dennen"
                    }`}
                  />
                  {index < data.timeline.length - 1 && (
                    <i className="absolute top-4 bottom-0 left-[3.5px] block w-px bg-lijn" />
                  )}
                  <span className="block font-mono text-[11px] text-mist">
                    {formatInBrussels(new Date(event.at))}
                    {event.actorName && ` · ${event.actorName}`}
                  </span>
                  <span className="font-semibold text-[13px]">{event.label}</span>
                </li>
              ))}
            </ol>
          </Card>

          <Card title={nl.beheer.detail.waarborg}>
            {data.deposit === null ? (
              <p className="text-[13px]">
                {data.isSystemParty
                  ? nl.beheer.detail.waarborgEigenWerking
                  : nl.beheer.detail.waarborgToekomst}
              </p>
            ) : (
              <dl>
                <KV label={nl.beheer.detail.waarborgGevraagd}>
                  <span className="font-mono">
                    {formatEuros(asCents(data.deposit.requestedCents))}
                  </span>
                </KV>
                <KV label={nl.beheer.detail.waarborgOntvangen}>
                  <span className="font-mono">
                    {formatEuros(asCents(data.deposit.receivedCents))}
                  </span>
                </KV>
                {data.deposit.withheldCents > 0 && (
                  <KV label={nl.beheer.detail.waarborgIngehouden}>
                    <span className="font-mono">
                      {formatEuros(asCents(data.deposit.withheldCents))}
                    </span>
                    {data.deposit.withheldReason && (
                      <span className="block text-[11.5px] text-mist">
                        {data.deposit.withheldReason}
                      </span>
                    )}
                  </KV>
                )}
                {data.deposit.refundedCents > 0 && (
                  <KV label={nl.beheer.detail.waarborgTerugbetaald}>
                    <span className="font-mono">
                      {formatEuros(asCents(data.deposit.refundedCents))}
                    </span>
                  </KV>
                )}
              </dl>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

const VELD =
  "min-h-11 w-full rounded-lg border-2 border-lijn bg-kaart px-3 text-[14px] focus:border-dennen focus:outline-none";

/**
 * The amendment form (0.2b): contact details and the purpose. Dates and
 * headcount are deliberately not here — they collide with frozen prices and
 * occupancy, and wait for the void-contract design. The server records WHICH
 * fields changed in the audit trail, never the values (NFR-6).
 */
function WijzigGegevens({
  boekingId,
  data,
  onKlaar,
}: {
  boekingId: string;
  data: AdminBookingDetail;
  onKlaar: () => void;
}) {
  const queryClient = useQueryClient();
  const [velden, setVelden] = useState({
    groepsnaam: data.partyName,
    contactNaam: data.party.contactName,
    contactEmail: data.party.contactEmail,
    contactTelefoon: data.party.contactPhone,
    doel: data.purpose ?? "",
  });

  const bewaar = useMutation({
    mutationFn: () => {
      // Trim before diffing — the server trims too, so an untrimmed compare
      // would send "changes" the server normalises back to what was stored.
      const wijziging: GegevensWijziging = {};
      const trimmed = {
        groepsnaam: velden.groepsnaam.trim(),
        contactNaam: velden.contactNaam.trim(),
        contactEmail: velden.contactEmail.trim(),
        contactTelefoon: velden.contactTelefoon.trim(),
        doel: velden.doel.trim(),
      };
      if (trimmed.groepsnaam !== data.partyName) wijziging.groepsnaam = trimmed.groepsnaam;
      if (trimmed.contactNaam !== data.party.contactName) {
        wijziging.contactNaam = trimmed.contactNaam;
      }
      if (trimmed.contactEmail !== data.party.contactEmail) {
        wijziging.contactEmail = trimmed.contactEmail;
      }
      if (trimmed.contactTelefoon !== data.party.contactPhone) {
        wijziging.contactTelefoon = trimmed.contactTelefoon;
      }
      if (trimmed.doel !== (data.purpose ?? "")) {
        wijziging.doel = trimmed.doel === "" ? null : trimmed.doel;
      }
      if (Object.keys(wijziging).length === 0) return Promise.resolve({ aangepast: [] });
      return patchGegevens(boekingId, wijziging);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-booking", boekingId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      onKlaar();
    },
  });

  // Native validation mirrors the API's zod bounds, so the admin hears about
  // a cleared name from the field itself instead of a generic 400.
  const invoer = (
    naam: keyof typeof velden,
    label: string,
    eisen: { type?: string; minLength?: number; required?: boolean } = {},
  ) => (
    <label className="block">
      <span className="mb-1 block font-semibold text-xs">{label}</span>
      <input
        value={velden[naam]}
        onChange={(e) => setVelden((huidig) => ({ ...huidig, [naam]: e.target.value }))}
        type={eisen.type ?? "text"}
        minLength={eisen.minLength}
        required={eisen.required ?? false}
        className={VELD}
      />
    </label>
  );

  return (
    <div className="mt-3 flex flex-col gap-2.5 border-lijn border-t pt-3">
      {invoer("groepsnaam", nl.aanvraag.velden.groepsnaam, { minLength: 2, required: true })}
      {invoer("contactNaam", nl.aanvraag.velden.contactNaam, { minLength: 2, required: true })}
      {invoer("contactEmail", nl.aanvraag.velden.email, { type: "email", required: true })}
      {invoer("contactTelefoon", nl.aanvraag.velden.telefoon, { minLength: 6, required: true })}
      {invoer("doel", nl.beheer.detail.doel)}
      {bewaar.isError && (
        <p className="font-semibold text-[12.5px] text-rood">
          {bewaar.error instanceof ApiError ? bewaar.error.message : nl.algemeen.fout}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={bewaar.isPending}
          onClick={() => bewaar.mutate()}
          className="min-h-11 rounded-lg bg-rood px-4 font-semibold text-kaart text-sm disabled:opacity-50"
        >
          {nl.beheer.detail.opslaan}
        </button>
        <button
          type="button"
          onClick={onKlaar}
          className="min-h-11 rounded-lg border-2 border-lijn px-4 font-semibold text-sm"
        >
          {nl.beheer.detail.annuleer}
        </button>
      </div>
    </div>
  );
}

/**
 * The pre-check the persona review missed on the enquiry screen: the red
 * "Neem optie" invited a tap that could only 409. A pre-check as UX; the
 * exclusion constraint stays the guarantee (rules/db.md).
 */
function ConflictWaarschuwing({ data }: { data: AdminBookingDetail }) {
  const lijst = useQuery({ queryKey: ["admin-bookings"], queryFn: fetchAdminBookings });
  const blokkades = useQuery({ queryKey: ["admin-blokkades"], queryFn: fetchAdminBlokkades });
  if (!lijst.data) return null;

  const conflicten = overlappendeBezetting(
    data,
    lijst.data.bookings,
    blokkades.data?.blokkades ?? [],
  );
  if (conflicten.boekingen.length === 0 && conflicten.blokkades.length === 0) return null;

  return (
    <div className="mb-3 rounded-lg bg-[#f3e7ce] px-3 py-2 font-semibold text-[#7c5510] text-[12.5px]">
      {conflicten.boekingen.map((conflict, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the same group can conflict twice with the same status
        <p key={`${conflict.naam}-${conflict.status}-${index}`}>
          ⚠{" "}
          {nl.beheer.conflict.boeking(
            conflict.naam,
            (nl.status as Record<string, string>)[conflict.status] ?? conflict.status,
          )}
        </p>
      ))}
      {conflicten.blokkades.map((reden) => (
        <p key={reden}>⚠ {nl.beheer.conflict.blokkade(reden)}</p>
      ))}
    </div>
  );
}

/**
 * After every click the admin owes the group a mail, and composing it was
 * homework the app silently left behind (persona review). One tap puts a
 * ready Dutch message on the clipboard — no mail server, no cost.
 */
function KopieerBericht({ data, totaalCents }: { data: AdminBookingDetail; totaalCents: number }) {
  const [toestand, setToestand] = useState<"idle" | "gekopieerd" | "mislukt">("idle");
  if (data.isSystemParty) return null;

  const periode = formatDateRange(data.arrivalDate, data.departureDate);
  const totaal = formatEuros(asCents(totaalCents));
  let bericht: string | null = null;
  if (data.status === "option" && data.optionExpiresAt !== null) {
    bericht = nl.beheer.berichten.option({
      naam: data.party.contactName,
      periode,
      totaal,
      vervaldag: formatInBrussels(new Date(data.optionExpiresAt)),
    });
  } else if (data.status === "confirmed" && data.deposit !== null) {
    bericht = nl.beheer.berichten.confirmed({
      naam: data.party.contactName,
      periode,
      totaal,
      waarborg: formatEuros(asCents(data.deposit.requestedCents)),
    });
  } else if (data.status === "settled" && data.deposit !== null) {
    // A negative balance means the deposit did not cover it — the mail must
    // name what is still owed instead of closing the books (review find).
    bericht =
      data.balanceCents !== null && data.balanceCents < 0
        ? nl.beheer.berichten.settledTekort({
            naam: data.party.contactName,
            periode,
            ingehouden: formatEuros(asCents(data.deposit.withheldCents)),
            tekort: formatEuros(asCents(Math.abs(data.balanceCents))),
          })
        : nl.beheer.berichten.settled({
            naam: data.party.contactName,
            periode,
            ingehouden: formatEuros(asCents(data.deposit.withheldCents)),
            terug: formatEuros(asCents(data.deposit.refundedCents)),
          });
  }
  if (bericht === null) return null;
  const tekst = bericht;

  const kopieer = async () => {
    // navigator.clipboard exists only in secure contexts — an admin on a
    // phone over plain http (the demo-LAN path, NFR-5) has none. Fail into
    // showing the text for manual copy rather than a silently dead button.
    try {
      if (!navigator.clipboard) throw new Error("geen clipboard");
      await navigator.clipboard.writeText(tekst);
      setToestand("gekopieerd");
    } catch {
      setToestand("mislukt");
    }
  };

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => void kopieer()}
        className="min-h-11 rounded-lg border-2 border-lijn px-4 font-semibold text-sm hover:border-dennen"
      >
        📋 {nl.beheer.berichten.knop}
      </button>
      {toestand === "gekopieerd" && (
        <span className="ml-3 font-semibold text-[12.5px] text-dennen">
          {nl.beheer.berichten.gekopieerd}
        </span>
      )}
      {toestand === "mislukt" && (
        <div className="mt-2">
          <p className="font-semibold text-[12.5px] text-rood">
            {nl.beheer.berichten.kopieMislukt}
          </p>
          <textarea
            readOnly
            value={tekst}
            rows={6}
            className="mt-1 w-full max-w-xl rounded-lg border-2 border-lijn bg-kaart p-2 text-[12.5px]"
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      )}
    </div>
  );
}
